if (!customElements.get('tabs-component')) {
  class TabsComponent extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.tabScrollable = this.querySelector('.tab-scrollable');
      this.tabsHeader = this.querySelector('.tabs-header');
      this.tabsContent = this.querySelector('.tabs-content');
      this.indicator = this.querySelector('.indicator');

      // ensure required elements exist
      if (!this.tabsHeader || !this.tabsContent || !this.indicator) {
        console.warn('Missing required tab elements');
        return;
      }

      // Click handler
      this.tabsHeader.addEventListener('click', (e) => {
        const btn = e.target.closest('.tab-button');
        if (btn) this.activateTab(btn);
      });

      // Keyboard navigation
      this.tabsHeader.addEventListener('keydown', (e) => {
        if (!e.target.classList.contains('tab-button')) return;
        this.handleKeyDown(e);
      });

      // Shopify editor support
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect.bind(this));
      }

      // Resize handler
      window.addEventListener('resize', () => this.positionIndicator());

      // Scroll handler for mobile - update indicator when scrolling the tabs
      this.tabScrollable.addEventListener('scroll', () => {
        this.positionIndicator();
      });

      // Initialize - use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        const firstTab = this.tabsHeader.querySelector('.tab-button.active') ||
                        this.tabsHeader.querySelector('.tab-button');
        if (firstTab) {
          this.activateTab(firstTab, true); // true = skip animation on init
        }
      }, 100);
    }

    disconnectedCallback() {
      // Cleanup not needed as element is being removed
    }

    activateTab(tab, skipAnimation = false) {
      if (!tab) return;

      const tabId = tab.getAttribute('data-tab');

      // Update tab buttons
      const buttons = this.tabsHeader.querySelectorAll('.tab-button');
      buttons.forEach((btn) => {
        const isActive = btn === tab;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        btn.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      // Update tab content
      const contents = this.tabsContent.querySelectorAll('.tab-content');
      contents.forEach((content) => {
        const isActive = content.getAttribute('id') === tabId;
        content.classList.toggle('active', isActive);
        content.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      // Position indicator
      this.positionIndicator(tab, skipAnimation);
    }

    positionIndicator(activeTab, skipAnimation = false) {
      if (!activeTab) {
        activeTab = this.tabsHeader.querySelector('.tab-button.active');
      }

      if (!activeTab || !this.indicator || !this.tabsHeader || !this.tabScrollable) {
        console.warn('Cannot position indicator - missing elements');
        return;
      }

      // Use getBoundingClientRect for accurate positioning across parent elements
      const tabRect = activeTab.getBoundingClientRect();
      const scrollableRect = this.tabScrollable.getBoundingClientRect();

      // Calculate position accounting for scroll offset
      const scrollOffset = this.tabScrollable.scrollLeft;
      const left = tabRect.left - scrollableRect.left + scrollOffset;
      const width = tabRect.width;

      console.log('Positioning indicator:', { left, width, scrollOffset });

      // Disable transition for initial positioning
      if (skipAnimation) {
        this.indicator.style.transition = 'none';
      }

      // Set position using transform for better performance
      this.indicator.style.width = `${width}px`;
      this.indicator.style.transform = `translateX(${left}px)`;

      // Re-enable transition
      if (skipAnimation) {
        // Force reflow
        this.indicator.offsetHeight;
        this.indicator.style.transition = '';
      }
    }

    handleKeyDown(e) {
      const buttons = Array.from(this.tabsHeader.querySelectorAll('.tab-button'));
      const idx = buttons.indexOf(e.target);
      if (idx === -1) return;

      let newIndex = idx;
      if (e.key === 'ArrowRight') newIndex = (idx + 1) % buttons.length;
      if (e.key === 'ArrowLeft') newIndex = (idx - 1 + buttons.length) % buttons.length;
      if (e.key === 'Home') newIndex = 0;
      if (e.key === 'End') newIndex = buttons.length - 1;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.activateTab(buttons[idx]);
        return;
      }

      if (newIndex !== idx) {
        e.preventDefault();
        buttons[newIndex].focus();
        this.activateTab(buttons[newIndex]);
      }
    }

    handleBlockSelect(e) {
      const blockId = e.detail.blockId;
      const tab = this.tabsHeader.querySelector(`[data-tab="tab-${blockId}"]`);
      if (tab) this.activateTab(tab);
    }
  }

  customElements.define('tabs-component', TabsComponent);
}
